import { useMemo, useState } from "react";
import MotionSection from "../components/common/MotionSection";
import SeoHead from "../components/common/SeoHead";
import SectionHeading from "../components/common/SectionHeading";
import TestimonialCard from "../components/testimonials/TestimonialCard";
import TestimonialsFinalCta from "../components/testimonials/TestimonialsFinalCta";
import TestimonialsHero from "../components/testimonials/TestimonialsHero";
import TestimonialsTrustSection from "../components/testimonials/TestimonialsTrustSection";
import useTestimonials from "../hooks/useTestimonials";

function TestimonialsPage() {
  const { testimonials, isLoading, error } = useTestimonials();
  const [countryFilter, setCountryFilter] = useState("All");

  const countries = useMemo(() => {
    const unique = new Set(testimonials.map((item) => item.country).filter(Boolean));
    return ["All", ...Array.from(unique).sort()];
  }, [testimonials]);

  const filteredTestimonials = useMemo(() => {
    if (countryFilter === "All") {
      return testimonials;
    }
    return testimonials.filter((item) => item.country === countryFilter);
  }, [testimonials, countryFilter]);

  return (
    <>
      <SeoHead
        title="Testimonials | NextEra Abroad"
        description="Read real student stories from NextEra Abroad and discover how expert guidance helps students and families plan global education with confidence."
        path="/testimonials"
        keywords={[
          "study abroad testimonials",
          "student success stories",
          "overseas education reviews",
          "nextera abroad testimonials"
        ]}
      />

      <TestimonialsHero />

      <MotionSection className="py-16 md:py-20">
        <div className="mx-auto w-full max-w-7xl px-6">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <SectionHeading
              center={false}
              kicker="Student Testimonials"
              title="Experiences from Students Across Destinations"
              description="Browse authentic student voices and see how structured support helps students move forward with confidence."
            />

            <div className="w-full md:w-[240px]">
              <label htmlFor="countryFilter" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-text/70">
                Filter by Country
              </label>
              <select
                id="countryFilter"
                value={countryFilter}
                onChange={(event) => setCountryFilter(event.target.value)}
                className="w-full rounded-xl border border-midnight-navy/15 bg-white px-4 py-3 text-sm text-midnight-navy focus:border-royal-blue focus:outline-none focus:ring-2 focus:ring-royal-blue/25"
              >
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isLoading ? (
            <div className="mt-10 rounded-2xl border border-midnight-navy/10 bg-white/90 px-6 py-12 text-center shadow-soft">
              <p className="text-lg font-semibold text-midnight-navy">Loading testimonials...</p>
            </div>
          ) : error ? (
            <div className="mt-10 rounded-2xl border border-red-200 bg-red-50 px-6 py-12 text-center shadow-soft">
              <p className="text-lg font-semibold text-red-700">{error}</p>
              <p className="mt-2 text-sm text-red-600">Please try again shortly.</p>
            </div>
          ) : filteredTestimonials.length === 0 ? (
            <div className="mt-10 rounded-2xl border border-midnight-navy/10 bg-white/90 px-6 py-12 text-center shadow-soft">
              <p className="text-lg font-semibold text-midnight-navy">No testimonials found for this filter.</p>
              <p className="mt-2 text-sm text-slate-text/75">Try choosing a different destination to view student stories.</p>
            </div>
          ) : (
            <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filteredTestimonials.map((testimonial) => (
                <TestimonialCard key={testimonial.id} testimonial={testimonial} />
              ))}
            </div>
          )}
        </div>
      </MotionSection>

      <TestimonialsTrustSection />
      <TestimonialsFinalCta />
    </>
  );
}

export default TestimonialsPage;
